import { StockSource, StockSettings, ErpFrame, ErpTemple } from '@ca/common'
import {
  ConditionDictionary,
  ConfigCondition,
  RegionConfig,
  StockConfig,
  VariableValues,
} from '@ca/common/types'
import { OrderState } from '@order/hooks/state/orderState'
import { OrderFrame, OrderItem } from '@order/orderTypes'
import checkRequirements from '@order/utils/checkRequirements'
import { debugConditions } from '@order/utils/getDebugLog'
import { validateItem } from '@order/utils/validateItem'
import FrameUtils, { FrameQty } from './FrameUtils'
import partition from 'lodash/partition'
import { CartItem } from '@order/hooks/useCart'

export const DEFAULT_STOCK_SOURCE: StockSource | null = 'global'

export const QTY_INFO = 10 // LTE display neutral frame quantity status
export const QTY_LOW = 3 // LTE display warning frame quantity status

export const QTY_SHOW_MIN = 1 // GTE to display frame quantity
export const QTY_SHOW_MAX = 10 // LTE to display frame quantity

export const QTY_SHOW_MIN_ONS = 2 // GTE to display frame quantity for ONS (excludes test frames)

export type StockItemType = 'frame' | 'temple'

export type StockSettingsKey = `${StockItemType}:${string}`

const getItemKey = (orderId: string, itemType: StockItemType = 'frame') => {
  return `${itemType}:${orderId}` satisfies StockSettingsKey
}

const DEBUG_IGNORE_QTY_CHECKS = false // AppConfig.isDebug

type OrderStockInfo = Pick<OrderState, 'items' | 'stock'>

export type FrameStatus =
  | 'selected'
  | 'saved'
  | 'available'
  | 'limited'
  | 'out'
  | 'minPrice'
  | 'maxPrice'
  | 'requirements'

const getRequiredQty = (
  stockConfig: StockConfig
): keyof FrameQty | undefined => {
  if (stockConfig.qty === 'total' || stockConfig.qty === 'require') {
    return 'total'
  }
  if (stockConfig.qty === 'store') {
    return 'store'
  }
  if (stockConfig.qty === 'remote') {
    return 'remote'
  }
  return undefined
}

const DefaultStockConfigs: StockConfig[] = [
  {
    type: 'global',
    qty: 'ignore',
    plc: ['ONS'],
    requirements: [],
  },
  {
    type: 'global',
    qty: 'remote',
    plc: [],
    requirements: [],
  },
  {
    type: 'local',
    qty: 'store',
    plc: [],
    requirements: [],
  },
]

export type StockState = Record<StockSettingsKey, StockSettings>

type StockOptions = {
  enabled?: boolean
  itemId?: string
  type?: StockItemType
  stock?: StockState
}

export type StockItem = OrderFrame | ErpFrame | ErpTemple

type FrameValidation = Pick<ConfigCondition, 'requires' | 'condition'>

type FrameValidationContext = {
  conditions: ConditionDictionary
  values?: VariableValues
}

const stockItemIsFrame = (item: StockItem): item is OrderFrame | ErpFrame => {
  if ('default_code' in item) {
    return true
  }
  return false
}

export const StockUtils = {
  getItemKey,
  stockItemIsFrame,
  adjustFrameOrder: <T extends StockItem>(
    frame: T,
    config: RegionConfig | undefined,
    state: OrderStockInfo | undefined,
    options?: StockOptions
  ) => {
    if (options?.enabled === false || !state) {
      return frame
    }
    const stock = options?.stock || state.stock
    // Check if global is available for qty = 0
    const globalAvailable = StockUtils.sources.global(
      {
        ...frame,
        qty: 0,
      },
      config
    )
    const relevantItems = state.items.filter((i) => {
      // Alow to skip self
      if (options?.itemId === i.id) {
        return false
      }
      // If global does not depend on qty we can assume that
      // for global source this is should not be counted against the qty
      const key = getItemKey(i.id, options?.type)

      if (globalAvailable && stock[key]?.source === 'global') {
        return false
      }
      return i.frame?.id === frame.id
    })

    const [global, local] = partition(relevantItems, (i) => {
      // If there is no source the frame would be counted as local we should verify this
      const key = getItemKey(i.id, options?.type)

      return stock[key]?.source === 'global'
    })

    return {
      ...frame,
      // Adjust the frame qty values based on the source selection
      // qty is the total qty of the frame so we need to subtract both global and local qty
      qty: Math.max(0, frame.qty - global.length - local.length),
      store_qty: Math.max(0, frame.store_qty - local.length),
    }
  },
  adjustStock: <T extends StockItem>(
    frame: T,
    config: RegionConfig | undefined,
    current: OrderStockInfo | undefined,
    saved: CartItem[],
    options?: StockOptions
  ) => {
    let adjusted = { ...frame }
    const unpaid = saved.filter((s) => !s.payment)

    if (current) {
      adjusted = StockUtils.adjustFrameOrder(frame, config, current, options)
    }

    if (unpaid.length > 0) {
      unpaid.forEach((item) => {
        adjusted = StockUtils.adjustFrameOrder(
          frame,
          config,
          item.order,
          options
        )
      })
    }

    return adjusted
  },
  validateType: (frame: StockItem, stockConfig?: StockConfig) => {
    if (!stockConfig) {
      return true
    }
    /**
     * We could check OrderValues but that would involve a lot of drilling
     * we would still need to get the frame values off the passed frame
     * not of the order
     */
    if (stockConfig.requirements && stockConfig.requirements.length > 0) {
      // FIXME: Get temple values
      const values = stockItemIsFrame(frame) ? FrameUtils.getValues(frame) : {}
      const isValid = checkRequirements(
        {
          requires: stockConfig.requirements,
        },
        values
      )

      if (!isValid) {
        return false
      }
    }
    // TODO: Should we detect temple type and get the quantity differently?
    const quantities = FrameUtils.getQty(frame)

    const requiredQty = getRequiredQty(stockConfig)

    if (
      requiredQty &&
      quantities[requiredQty] === 0 &&
      !DEBUG_IGNORE_QTY_CHECKS
    ) {
      return false
    }

    if (
      stockConfig.plc &&
      Array.isArray(stockConfig.plc) &&
      stockConfig.plc.length > 0 &&
      !stockConfig.plc.includes(frame.plc)
    ) {
      return false
    }

    return true
  },
  sources: {
    global: (frame: StockItem, config: RegionConfig | undefined) => {
      const stockConfigs = config?.stock || DefaultStockConfigs
      const globalStockConfigs = stockConfigs?.filter(
        (cfg) => cfg.type === 'global'
      )

      if (!globalStockConfigs?.length) {
        return frame.plc === 'ONS'
      }
      return globalStockConfigs.some((cfg) =>
        StockUtils.validateType(frame, cfg)
      )
    },
    local: (frame: StockItem, config: RegionConfig | undefined) => {
      const stockConfigs = config?.stock || DefaultStockConfigs
      const localStockConfigs = stockConfigs.filter(
        (cfg) => cfg.type === 'local'
      )
      if (!localStockConfigs?.length) {
        return frame.qty > 0
      }
      return localStockConfigs.some((cfg) =>
        StockUtils.validateType(frame, cfg)
      )
    },
  },
  getValidSources: (
    frame: StockItem,
    type: StockConfig['type'],
    configs: StockConfig[]
  ) => {
    return configs.filter(
      (cfg) => cfg.type === type && StockUtils.validateType(frame, cfg)
    )
  },

  isAvailable: (item: StockItem, config: RegionConfig | undefined) => {
    if (!config?.stock) {
      // Default fallback logic
      return item.qty > 0 || item.plc === 'ONS'
    }

    return (
      StockUtils.sources.global(item, config) ||
      StockUtils.sources.local(item, config)
    )
  },
  validateFrame: (
    source: StockSource,
    item: OrderItem,
    config: RegionConfig | undefined
  ) => {
    if (!item.frame) {
      return false
    }

    const valid = StockUtils.sources[source](item.frame, config)
    return valid
  },
  validateTemple: (
    source: StockSource,
    item: OrderItem,
    config: RegionConfig | undefined
  ) => {
    if (!item.temple) {
      return false
    }
    const valid = StockUtils.sources[source](item.temple, config)
    return valid
  },

  validate: (
    source: StockSource,
    item: OrderItem,
    config: RegionConfig | undefined,
    type: StockItemType = 'frame'
  ) => {
    if (type === 'frame') {
      return StockUtils.validateFrame(source, item, config)
    }
    if (type === 'temple') {
      return StockUtils.validateTemple(source, item, config)
    }
    return false
  },

  getState: (
    item: OrderItem,
    itemStock: StockSettings,
    config: RegionConfig | undefined,
    type: StockItemType = 'frame'
  ) => {
    if (!itemStock) {
      if (item.frame?.qty === 0) {
        return 'positive'
      }
      return 'neutral'
    }
    if (itemStock.source === 'global') {
      return StockUtils.validate('global', item, config, type)
        ? 'positive'
        : 'negative'
    }

    const valid = StockUtils.validate(itemStock.source, item, config, type)

    return valid && itemStock.inventory && itemStock.trace
      ? 'positive'
      : 'neutral'
  },
  getStock: (
    source: StockSource,
    item: OrderItem,
    type: StockItemType = 'frame'
  ) => {
    if (source === 'global') {
      return {
        inventory: '',
        source,
        trace: false,
      } satisfies StockSettings
    }

    const code = type === 'frame' ? item.frame?.default_code : item.temple?.name
    return {
      inventory: code || '',
      source,
      trace: false,
    }
  },
  getDefaultStock: (
    item: OrderItem,
    config: RegionConfig | undefined,
    type: StockItemType = 'frame'
  ) => {
    if (type === 'frame' && !item.frame) {
      return
    }
    if (type === 'temple' && !item.temple) {
      return
    }

    const isValid = StockUtils.validate(
      DEFAULT_STOCK_SOURCE,
      item,
      config,
      type
    )
    if (!isValid) {
      return
    }
    return StockUtils.getStock(DEFAULT_STOCK_SOURCE, item, type)
  },
  getFrameStatus: (
    frame: OrderFrame | ErpFrame | undefined,
    config: RegionConfig | undefined,
    options: {
      minPrice?: number
      maxPrice?: number
      validation?: FrameValidation
      context?: FrameValidationContext
    }
  ): {
    disabled: boolean
    status: FrameStatus
  } => {
    if (!frame) {
      return {
        disabled: true,
        status: 'out',
      }
    }
    const disabled = !StockUtils.isAvailable(frame, config)

    if (disabled) {
      return {
        disabled,
        status: 'out',
      }
    }

    if (options?.minPrice && frame.list_price < options.minPrice) {
      return {
        disabled: true,
        status: 'minPrice',
      }
    }
    if (options?.maxPrice && frame.list_price > options.maxPrice) {
      return {
        disabled: true,
        status: 'maxPrice',
      }
    }

    if (options.validation) {
      const { validation, context } = options
      const frameValues = FrameUtils.getValues(frame)
      const values = {
        ...context?.values,
        ...frameValues,
      }

      if (
        validation.condition &&
        !context?.conditions[validation.condition]
      ) {
        debugConditions.warn('Frame validation condition is missing', {
          condition: validation.condition,
        })
        return {
          disabled: true,
          status: 'requirements',
        }
      }

      const valid = validateItem(
        validation,
        values,
        context?.conditions ?? {},
        undefined
      ).result
      if (!valid) {
        return {
          disabled: true,
          status: 'requirements',
        }
      }
    }

    return {
      disabled: false,
      status: 'available',
    }
  },
}

export const showFrameQty = (
  frame: ErpFrame | null | undefined,
  config: RegionConfig | undefined
): boolean => {
  if (!frame) {
    return false
  }
  const local = StockUtils.sources.local(frame, config)
  if (!local) {
    return false
  }
  const { store_qty, plc } = frame

  if (store_qty === undefined) return false
  if (plc === 'ONS' && store_qty < QTY_SHOW_MIN_ONS) return false
  return store_qty >= QTY_SHOW_MIN && store_qty <= QTY_SHOW_MAX
}

export const showAdjustedFrameQty = (
  frame: ErpFrame | null | undefined,
  config: RegionConfig | undefined,
  state: Pick<OrderState, 'stock' | 'items'> | undefined,
  cart: CartItem[]
): boolean => {
  if (!frame) {
    return false
  }
  const adjusted = StockUtils.adjustStock(frame, config, state, cart)

  return showFrameQty(adjusted, config)
}
